import { useEffect, useMemo, useRef, useState } from "react";

// Context
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Hooks
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

// API
import { onCreateDraws } from "@DataPages/api/draw";

// Components
import BodyLayer from "@Layouts/components/Layers/BodyLayer";
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import PayPeriodSideBar from "@Layouts/components/SideBars/PayPeriodSideBar";
import DrawBody from "./DrawBody";

// Types
import { IPeriodTreeLeaf } from "@Layouts/components/SideBars/PayPeriodSideBar/interfaces";
import { Nullable } from "@Common/interfaces/types.ts";

const QUERY_IDENTIFIERS = ["draws", "overdraws"];

export default function DrawLayer() {
	const { clearCache } = useCacheCleaner();
	const lastSelected = useRef<Nullable<IPeriodTreeLeaf>>(null);
	const [selection, setSelection] = useState<Nullable<IPeriodTreeLeaf>>(null);
	const { addSubscription, clearSubscriptionContext } = useSubscriptions();

	const sideBar = useMemo(() => {
		const sideSelection = function (selected?: IPeriodTreeLeaf | null, isActiveCycle = false) {
			const payPeriodId = selected?.payPeriodId;
			const commissionCycleId = selected?.cycleId;

			if (selected?._id !== lastSelected.current?._id) {
				lastSelected.current = selected;
				setSelection(selected);

				if (isActiveCycle) {
					addSubscription(
						["draws", commissionCycleId, payPeriodId],
						[
							{
								context: "onCreateDraws",
								query: onCreateDraws,
								variables: {
									commissionCycleId: selected?.cycleId,
									payPeriodId: selected?.payPeriodId,
								},
							},
						]
					);
				}
			}
		};

		return <PayPeriodSideBar callbackSelected={sideSelection} selectDefaultLastCommissionCycle isMultiple />;
	}, [addSubscription]);

	useEffect(() => {
		return () => {
			setSelection(null);
			lastSelected.current = null;
			clearCache(QUERY_IDENTIFIERS);
			clearSubscriptionContext();
		};
	}, [clearSubscriptionContext]);

	return (
		<BodyLayer fixedContent={sideBar}>
			<RoundedBox sx={{ minHeight: "630px", position: "relative" }}>
				<DrawBody
					selectedPayPeriod={selection}
					isUpdatable={Boolean(selection?.cycleStatus === "CLOSING" || selection?.cycleStatus === "ACTIVE")}
				/>
			</RoundedBox>
		</BodyLayer>
	);
}
